/**
 * Key hierarchy and content encryption (privacy requirements PR-01 and PR-03).
 *
 *   Content key (CK)  one per photo, AES-256-GCM over the image bytes
 *   Master key  (MK)  AES-KW, wraps every CK. Never leaves the device in clear
 *   Recovery key(RK)  256 bits of entropy shown once, never transmitted
 *
 * The server only ever holds ciphertext and wrap(MK, CK). Deleting a photo means
 * destroying its CK, which leaves the ciphertext permanently unreadable — that is
 * what makes the deletion claim a property rather than a promise.
 */

const subtle = globalThis.crypto.subtle;

export interface SealedBlob {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

export async function generateMasterKey(): Promise<CryptoKey> {
  return subtle.generateKey({ name: 'AES-KW', length: 256 }, false, ['wrapKey', 'unwrapKey']);
}

export async function generateContentKey(): Promise<CryptoKey> {
  // Extractable: it must be wrappable for sync, and it is only ever wrapped.
  return subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

export async function wrapContentKey(mk: CryptoKey, ck: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await subtle.wrapKey('raw', ck, mk, 'AES-KW'));
}

export async function unwrapContentKey(mk: CryptoKey, wrapped: Uint8Array): Promise<CryptoKey> {
  return subtle.unwrapKey('raw', wrapped, mk, 'AES-KW', { name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
}

export async function seal(ck: CryptoKey, plaintext: Uint8Array): Promise<SealedBlob> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await subtle.encrypt({ name: 'AES-GCM', iv }, ck, plaintext as unknown as BufferSource),
  );
  return { iv, ciphertext };
}

export async function open(ck: CryptoKey, blob: SealedBlob): Promise<Uint8Array> {
  return new Uint8Array(
    await subtle.decrypt(
      { name: 'AES-GCM', iv: blob.iv as unknown as BufferSource },
      ck,
      blob.ciphertext as unknown as BufferSource,
    ),
  );
}

/* ---------------------------------------------------------------- recovery key */

// Crockford base32, minus the letters that get misread when someone copies a
// recovery key off a screen or a printed sheet.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** 256 bits, grouped for transcription: XXXXX-XXXXX-… */
export function generateRecoveryKey(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 31];
  return (out.match(/.{1,5}/g) ?? []).join('-');
}

export function normaliseRecoveryKey(input: string): string {
  return input.toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
}

/**
 * Derives a wrapping key from a recovery key.
 *
 * The architecture map specifies Argon2id. WebCrypto does not provide it, so this
 * is PBKDF2-SHA512 behind a pluggable interface: swapping in an Argon2id WASM
 * build changes this function only. Recorded as a known deviation rather than
 * left as a silent downgrade — see the Phase 4 build log.
 */
export async function deriveWrappingKey(
  recoveryKey: string,
  salt: Uint8Array,
  iterations = 600_000,
): Promise<CryptoKey> {
  const material = await subtle.importKey(
    'raw',
    new TextEncoder().encode(normaliseRecoveryKey(recoveryKey)) as unknown as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as unknown as BufferSource, iterations, hash: 'SHA-512' },
    material,
    { name: 'AES-KW', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/* -------------------------------------------------------------- size padding */

/**
 * Pads to a bucket boundary so the stored object size stops revealing what kind
 * of photo it is (threat T1). A four-byte length prefix makes it reversible.
 */
export const BUCKET = 65_536;

export function pad(plain: Uint8Array, bucket = BUCKET): Uint8Array {
  const total = Math.ceil((plain.length + 4) / bucket) * bucket;
  const out = new Uint8Array(total);
  new DataView(out.buffer).setUint32(0, plain.length);
  out.set(plain, 4);
  return out;
}

export function unpad(padded: Uint8Array): Uint8Array {
  const len = new DataView(padded.buffer, padded.byteOffset, padded.byteLength).getUint32(0);
  if (len > padded.length - 4) throw new Error('Padding header is not consistent with the payload');
  return padded.subarray(4, 4 + len);
}
